import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdDeleteOutline } from "react-icons/md";
import axios from 'axios';
import { toast } from 'react-toastify';
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { getToken } from '../../../../utils/tokenService'; // Assuming you have a utility function to get the token

const AadharSection = ({ 
  user, 
  setUser, 
  setShowCropModal, 
  setPreviewUrl, 
  setCrop,
  isAadharFront,
  setIsAadharFront
}) => {
  const aadhar_front_image_input_ref = useRef();
  const aadhar_back_image_input_ref = useRef();
  const [isDigilockerLoading, setIsDigilockerLoading] = useState(false);
  const [showDigilockerSdk, setShowDigilockerSdk] = useState(false);

  const getAppScheme = useCallback(() => {
    const rawScheme = process.env.REACT_APP_APP_SCHEME || "com.complymax.app";
    return String(rawScheme).replace(/:\/+$/, "").trim() || "com.complymax.app";
  }, []);

  const extractClientIdFromCallbackUrl = useCallback((rawUrl) => {
    if (!rawUrl) return "";

    try {
      const parsed = new URL(rawUrl);
      const queryClientId =
        parsed.searchParams.get("client_id") ||
        parsed.searchParams.get("clientId") ||
        parsed.searchParams.get("reference_id");
      if (queryClientId) return String(queryClientId).trim();

      const hashValue = String(parsed.hash || "").replace(/^#/, "");
      if (!hashValue) return "";

      const hashParams = new URLSearchParams(hashValue);
      const hashClientId =
        hashParams.get("client_id") ||
        hashParams.get("clientId") ||
        hashParams.get("reference_id") ||
        "";

      return String(hashClientId).trim();
    } catch (error) {
      return "";
    }
  }, []);

  const loadDigilockerSdkScript = () =>
    new Promise((resolve, reject) => {
      if (window.DigiboostSdk) {
        resolve();
        return;
      }

      const existing = document.getElementById("digiboost-sdk-script");
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () =>
          reject(new Error("Failed to load DigiLocker SDK"))
        );
        return;
      }

      const script = document.createElement("script");
      script.id = "digiboost-sdk-script";
      script.src =
        "https://cdn.jsdelivr.net/gh/surepassio/surepass-digiboost-web-sdk@latest/index.min.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load DigiLocker SDK"));
      document.body.appendChild(script);
    });

  const handleAadharFrontCroppedImage = async (blob) => {
    const token = await getToken();
    try {
      const formData = new FormData();
      const file = new File([blob], "cropped-aadhar-front.jpeg", {
        type: "image/jpeg",
      });
      formData.append("image", file);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/upload/aadhar-front-image`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.user) {
        setUser((prev) => ({
          ...prev,
          aadhar_front_image: response.data.user.aadhar_front_image,
        }));
        toast.success("Aadhar front image uploaded successfully!");
        setShowCropModal(false);
        setPreviewUrl(null);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.message || "Failed to upload image");
    }
  };

  const handleAadharBackCroppedImage = async (blob) => {
    const token = await getToken();
    try {
      const formData = new FormData();
      const file = new File([blob], "cropped-aadhar-back.jpeg", {
        type: "image/jpeg",
      });
      formData.append("image", file);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/upload/aadhar-back-image`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.user) {
        setUser((prev) => ({
          ...prev,
          aadhar_back_image: response.data.user.aadhar_back_image,
        }));
        toast.success("Aadhar back image uploaded successfully!");
        setShowCropModal(false);
        setPreviewUrl(null);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.message || "Failed to upload image");
    }
  };

  const handlePdfUpload = async (file, isFront) => {
    const token = await getToken();
    try {
      const formData = new FormData();
      formData.append("image", file);

      const endpoint = isFront 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/user/upload/aadhar-front-image`
        : `${process.env.REACT_APP_BACKEND_URL}/api/user/upload/aadhar-back-image`;

      const response = await axios({
        method: 'post',
        url: endpoint,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });

      if (response.data.user) {
        setUser((prev) => ({
          ...prev,
          [isFront ? 'aadhar_front_image' : 'aadhar_back_image']: response.data.user[isFront ? 'aadhar_front_image' : 'aadhar_back_image'],
        }));
        toast.success(`Aadhar ${isFront ? 'front' : 'back'} document uploaded successfully!`);
        setShowCropModal(false);
        setPreviewUrl(null);
        setIsAadharFront(false);
      }
    } catch (error) {
      console.error("Upload error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      if (error.response?.status === 413) {
        toast.error("File size too large. Please upload a smaller file.");
      } else if (error.response?.status === 415) {
        toast.error("Unsupported file type. Please upload a PDF or image file.");
      } else {
        toast.error(error.response?.data?.message || "Failed to upload document");
      }
    }
  };

  const handleDeleteAadharFrontImage = async () => {
    const token = await getToken();
    try {
      console.log("Attempting to delete Aadhar front image...");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/delete/aadhar-front-image`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Delete response:", response);

      if (response.data.user) {
        setUser((prev) => ({
          ...prev,
          aadhar_front_image: null,
        }));
        toast.success("Aadhar front document deleted successfully!");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Delete error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(error.response?.data?.message || "Failed to delete document");
    }
  };

  const handleDeleteAadharBackImage = async () => {
    const token = await getToken();
    try {
      console.log("Attempting to delete Aadhar back image...");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/delete/aadhar-back-image`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Delete response:", response);

      if (response.data.user) {
        setUser((prev) => ({
          ...prev,
          aadhar_back_image: null,
        }));
        toast.success("Aadhar back document deleted successfully!");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Delete error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(error.response?.data?.message || "Failed to delete document");
    }
  };

  const handleDigilockerSuccess = useCallback(async (sdkData) => {
    try {
      const clientId =
        sdkData?.client_id ||
        sdkData?.data?.client_id ||
        sdkData?.response?.client_id ||
        "";

      if (!clientId) {
        toast.success("DigiLocker verification completed.");
        return;
      }

      const token = await getToken();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/profile/verify-aadhaar-digilocker`,
        { client_id: clientId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success && response.data?.user) {
        setUser(response.data.user);
        toast.success("Aadhaar verified from DigiLocker successfully!");
      } else {
        toast.error(response.data?.message || "Unable to sync DigiLocker KYC data.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "DigiLocker verification completed, but profile update failed."
      );
    }
  }, [setUser]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let listener;
    const setupAppUrlOpenListener = async () => {
      listener = await CapacitorApp.addListener("appUrlOpen", ({ url }) => {
        if (!url) return;
        const clientId = extractClientIdFromCallbackUrl(url);
        if (!clientId) return;

        // Check verification status from the deep link URL
        let verificationStatus = "success";
        try {
          verificationStatus =
            new URL(url).searchParams.get("status") || "success";
        } catch (_) {}

        if (verificationStatus !== "success") {
          toast.error(
            "DigiLocker verification was not completed. Please try again."
          );
          return;
        }

        handleDigilockerSuccess({ client_id: clientId });
      });
    };

    setupAppUrlOpenListener();

    // Pick up result that RouterLayout stored when the app came back from background
    const pendingClientId = String(
      localStorage.getItem("digilocker_client_id") || ""
    ).trim();
    const pendingStatus = String(
      localStorage.getItem("digilocker_verification_status") || "success"
    ).trim();

    if (pendingClientId || pendingStatus) {
      localStorage.removeItem("digilocker_client_id");
      localStorage.removeItem("digilocker_verification_status");

      if (pendingStatus !== "success") {
        toast.error(
          "DigiLocker verification was not completed. Please try again."
        );
      } else if (pendingClientId) {
        handleDigilockerSuccess({ client_id: pendingClientId });
      }
    }

    return () => {
      if (listener && typeof listener.remove === "function") {
        listener.remove();
      }
    };
  }, [extractClientIdFromCallbackUrl, handleDigilockerSuccess]);

  const handleStartDigilockerVerification = async () => {
    const token = await getToken();

    try {
      setIsDigilockerLoading(true);
      setShowDigilockerSdk(true);
      const initRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/profile/digilocker/initialize`,
        {
          signup_flow: true,
          skip_main_screen: false,
          // Always send HTTPS redirect URL — DigiLocker/Surepass rejects custom schemes.
          // The intermediate web page (DigilockerCallbackBridge) then redirects back to the app.
          ...(Capacitor.isNativePlatform()
            ? {
                redirect_url:
                  process.env.REACT_APP_DIGILOCKER_REDIRECT_URL ||
                  "https://complymax.co.in/digilocker-return",
              }
            : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const sdkToken = initRes?.data?.data?.token;
      if (!sdkToken) {
        toast.error("Failed to initialize DigiLocker session.");
        return;
      }

      await loadDigilockerSdkScript();

      if (!window.DigiboostSdk) {
        toast.error("DigiLocker SDK is not available.");
        return;
      }

      window.DigiboostSdk({
        gateway: process.env.REACT_APP_DIGILOCKER_GATEWAY || "production",
        token: sdkToken,
        selector: "#digilocker-sdk-trigger",
        onSuccess: handleDigilockerSuccess,
        onFailure: (error) => {
          toast.error(error?.message || "DigiLocker verification failed.");
        },
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error?.message ||
          "Unable to start DigiLocker verification."
      );
    } finally {
      setIsDigilockerLoading(false);
    }
  };

  const handleAadharFrontImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check file size (e.g., 5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toast.error("File size too large. Please upload a file smaller than 5MB.");
        e.target.value = ''; // Clear the file input
        return;
      }

      // Check if file is PDF
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        handlePdfUpload(file, true);
        e.target.value = ''; // Clear the file input
        return;
      }

      // For images, proceed with cropping
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setCrop({
          unit: 'px',
          x: 0,
          y: 0,
          width: 200,
          height: 150
        });
        setShowCropModal(true);
        setIsAadharFront(true);
        e.target.value = ''; // Clear the file input after setting preview
      };
      reader.onerror = () => {
        toast.error("Error reading file");
        e.target.value = ''; // Clear the file input on error
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAadharBackImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check file size (e.g., 5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toast.error("File size too large. Please upload a file smaller than 5MB.");
        e.target.value = ''; // Clear the file input
        return;
      }

      // Check if file is PDF
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        handlePdfUpload(file, false);
        e.target.value = ''; // Clear the file input
        return;
      }

      // For images, proceed with cropping
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setCrop({
          unit: 'px',
          x: 0,
          y: 0,
          width: 200,
          height: 150
        });
        setShowCropModal(true);
        setIsAadharFront(false);
        e.target.value = ''; // Clear the file input after setting preview
      };
      reader.onerror = () => {
        toast.error("Error reading file");
        e.target.value = ''; // Clear the file input on error
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className='id_sec'>
      <div className='pan_heading'>
        <h1>Aadhar Card</h1>
      </div>
      <div className="px-0 px-sm-5 mb-3">
        <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center mb-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleStartDigilockerVerification}
            disabled={isDigilockerLoading}
          >
            {isDigilockerLoading ? "Loading DigiLocker..." : "Verify with DigiLocker"}
          </button>
          <span className={`badge ${user.kyc ? "bg-success" : "bg-secondary"}`}>
            {user.kyc ? "KYC Verified" : "KYC Pending"}
          </span>
        </div>
        {showDigilockerSdk && (
          <div
            id="digilocker-sdk-trigger"
            className="border rounded p-2 mb-2"
            style={{ minHeight: "56px", background: "#fff" }}
          />
        )}
        <small className="text-muted">
          Digilocker Web SDK runs in-page for consent-based Aadhaar eKYC verification.
        </small>
      </div>
      <div className="profile-content-section d-flex gap-3 justify-content-center justify-content-sm-between flex-wrap px-0 px-sm-5">
        <div className="flex flex-col profile-content-box">
          <dt>Aadhar No.</dt>
          <dd>{user.aadhar_number}</dd>
        </div>
        <div className="flex flex-col profile-content-box">
          <dt>Name</dt>
          <dd>{user.full_Name}</dd>
        </div>
        <div className="flex flex-col profile-content-box position-relative">
          <dt>Aadhar Card Documents</dt>
          <div className="d-flex flex-column gap-3">
            {/* Front Image Section */}
          <dd className="d-flex align-items-center gap-2">
              <span className="text-muted me-2">Front:</span>
              {user.aadhar_front_image ? (
              <>
                <span className="text-success">
                  <i className="fas fa-check-circle"></i> Uploaded
                </span>
                <div className="d-flex gap-2">
                  <button 
                    className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        const url = `${process.env.REACT_APP_BACKEND_URL}${user.aadhar_front_image}`;
                        // Open PDF in a new tab
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                  >
                      View Front
                  </button>
                    {/* <button 
                    className="btn btn-primary btn-sm"
                      onClick={() => aadhar_front_image_input_ref.current.click()}
                  >
                    Update
                    </button> */}
                    {/* <button 
                    className="btn btn-outline-danger btn-sm"
                      onClick={handleDeleteAadharFrontImage}
                  >
                    <MdDeleteOutline size={18} />
                    </button> */}
                </div>
              </>
            ) : (
              <>
                <span className="text-warning">
                  <i className="fas fa-exclamation-circle"></i> Not uploaded
                </span>
                <button 
                    onClick={() => aadhar_front_image_input_ref.current.click()}
                    className="btn btn-primary btn-sm"
                  >
                    Upload Front
                  </button>
                </>
              )}
            </dd>

            {/* Back Image Section */}
            <dd className="d-flex align-items-center gap-2">
              <span className="text-muted me-2">Back:</span>
              {user.aadhar_back_image ? (
                <>
                  <span className="text-success">
                    <i className="fas fa-check-circle"></i> Uploaded
                  </span>
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        const url = `${process.env.REACT_APP_BACKEND_URL}${user.aadhar_back_image}`;
                        // Open PDF in a new tab
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      View Back
                    </button>
                    {/* <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => aadhar_back_image_input_ref.current.click()}
                    >
                      Update
                    </button> */}
                    {/* <button 
                      className="btn btn-outline-danger btn-sm"
                      onClick={handleDeleteAadharBackImage}
                    >
                      <MdDeleteOutline size={18} />
                    </button> */}
                  </div>
                </>
              ) : (
                <>
                  <span className="text-warning">
                    <i className="fas fa-exclamation-circle"></i> Not uploaded
                  </span>
                  <button 
                    onClick={() => aadhar_back_image_input_ref.current.click()}
                  className="btn btn-primary btn-sm"
                >
                    Upload Back
                </button>
              </>
            )}
            </dd>
          </div>

          {/* Hidden Input Fields */}
          <input 
            ref={aadhar_front_image_input_ref}
            type="file"
            onChange={handleAadharFrontImageChange}
            accept="image/*,.pdf"
            className="d-none"
          />
            <input 
            ref={aadhar_back_image_input_ref}
              type="file"
            onChange={handleAadharBackImageChange}
            accept="image/*,.pdf"
              className="d-none"
            />
        </div>
      </div>
    </div>
  );
};

export default AadharSection; 
